import DotLoader from 'src/components/reusable/dot-loader';
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import jsCookie from "js-cookie";
import { API_PROD_URL } from "src/utils/axios";
import { toast } from "react-hot-toast";

// This helper function remains unchanged.
const safeParseData = (raw?: string | null) => {
  if (!raw) return null;
  let decoded = raw;
  try {
    decoded = decodeURIComponent(raw);
  } catch (e) { /* ignore */ }
  try {
    return JSON.parse(decoded);
  } catch (e) { /* ignore */ }
  let s = decoded
    .replace(/\bNone\b/g, "null")
    .replace(/\bTrue\b/g, "true")
    .replace(/\bFalse\b/g, "false");
  s = s.replace(/'((?:\\'|[^'])*)'/g, (_, inner) => JSON.stringify(inner));
  try {
    return JSON.parse(s);
  } catch (err) {
    console.error("Failed to parse data query param", { raw, decoded, transformed: s, err });
    return { raw: decoded };
  }
};

const LinkedinSuccess = () => {
  const [searchParams] = useSearchParams();
  const [showAccounts, setShowAccounts] = useState(false);

  const status = searchParams.get("status");
  const data = searchParams.get("data");

  const parsed = useMemo(() => safeParseData(data), [data]);
  const effectiveStatus = status || (parsed && !parsed.error ? "success" : "error");

  useEffect(() => {
    if (window.opener) {
      try {
        window.opener.postMessage({ effectiveStatus, data: parsed }, window.location.origin);
      } catch (e) {
        window.opener.postMessage({ effectiveStatus, data: parsed }, '*');
      }
    }
  }, [effectiveStatus, parsed]);

  const profile = parsed?.user_profile || parsed?.userProfile || parsed?.user || null;
  const firstName = profile?.firstName || profile?.firstname || profile?.first_name || null;
  const lastName = profile?.lastName || profile?.lastname || profile?.last_name || null;
  const profilePic = profile?.profilePicture || profile?.profile_picture || profile?.picture || null;

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4 font-sans">
      <div className="rounded-lg max-w-xl w-full p-8">
        {effectiveStatus === "success" ? (
          <div className="text-center">
            <div className='flex w-full justify-center align-middle'>
              <svg className="w-10 h-10 text-green-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <h2 className="text-xl font-semibold text-gray-800 mb-1">Successfully Connected</h2>
              <p className="text-gray-500 mb-6">Your LinkedIn account has been linked.</p>
            </div>
            {profile && (
              <div className="flex items-center space-x-3 text-left py-4 mb-6">
                {profilePic ? (
                  <img src={profilePic} alt="Profile" className="w-12 h-12 rounded-full object-cover" />
                ) : (
                  <div className="w-12 h-12 rounded-full bg-gray-200 flex items-center justify-center text-gray-500 font-medium">
                    {firstName?.[0] || '?'}
                  </div>
                )}
                <div>
                  <h3 className="font-medium text-gray-800">
                    {firstName || lastName ? `${firstName || ""} ${lastName || ""}`.trim() : "LinkedIn User"}
                  </h3>
                </div>
              </div>
            )}

            <button
              onClick={() => setShowAccounts(!showAccounts)}
              className="w-full bg-[#442873] text-white text-sm font-medium py-2.5 px-4 rounded-md hover:opacity-90 transition-opacity focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#442873]"
            >
              {showAccounts ? "Hide Ad Accounts" : "Select Ad Account to Connect Campaign"}
            </button>

            {showAccounts && (
              <div className="pt-6 border-t border-gray-200">
                <LinkedInAccounts />
              </div>
            )}
          </div>
        ) : (
          <div className="text-center">
            <svg className="w-10 h-10 text-red-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h2 className="text-xl font-semibold text-gray-800 mb-1">Connection Failed</h2>
            <p className="text-gray-500">Please close this window and try again.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default LinkedinSuccess;

type LinkedInAccount = {
  id: string;
  name?: string;
  status?: string;
};

const LinkedInAccounts = () => {
  const userId = jsCookie.get("user_id");
  const [accounts, setAccounts] = useState<LinkedInAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedAccount, setSelectedAccount] = useState<string>("");
  const [loadingCampaigns, setLoadingCampaigns] = useState(false);
  const [campaigns, setCampaigns] = useState<any[]>([]);
  const [selectedCampaign, setSelectedCampaign] = useState(null);

  useEffect(() => {
    const fetchAccounts = async () => {
      if (!userId) {
        setError("User ID not found.");
        setLoading(false);
        return;
      }
      try {
        const response = await fetch(`${API_PROD_URL}/linkedin/accounts?user_id=${userId}`);
        if (!response.ok) throw new Error("Failed to fetch accounts.");
        const data = await response.json();
        setAccounts(data.accounts || []);
      } catch (err: any) {
        setError(err.message ?? "An unknown error occurred.");
      } finally {
        setLoading(false);
      }
    };
    fetchAccounts();
  }, [userId]);

  const handleAccountSelect = async (accountId: string) => {
    setSelectedAccount(accountId);
    setCampaigns([]);
    setLoadingCampaigns(true);
    try {
      const response = await fetch(`${API_PROD_URL}/linkedin/accounts/${accountId}/campaigns?user_id=${userId}`);
      if (!response.ok) throw new Error("Failed to fetch campaigns.");
      const data = await response.json();
      setCampaigns(data.campaigns || []);
    } catch (err: any) {
      console.error("Error fetching campaigns:", err);
    } finally {
      setLoadingCampaigns(false);
    }
  };

  if (loading) {
    return (
      <div className="py-6 text-center flex flex-col items-center justify-center">
        <DotLoader />
        <p className="mt-3 text-sm text-gray-500">Loading accounts...</p>
      </div>
    );
  }

  if (error) {
    return <p className="text-sm text-red-600 bg-red-50 p-3 rounded-md">{error}</p>;
  }

  if (accounts.length === 0) {
    return <p className="text-sm text-gray-500 bg-gray-100 p-3 rounded-md">No ad accounts found.</p>;
  }

  async function handleCampaignSelect(selectedCampaign: string, user_id: string) {
    const response = await fetch(`${API_PROD_URL}/marketing-agent/campaign/linkedin/save_campaign_id?userId=${user_id}&campaign_id=${selectedCampaign}`, {
    });
    if (!response.ok) {
      console.error("Error confirming campaign:", response.statusText);
    }
    if (response.ok) {
      toast.success("Campaign successfully linked!");
      if (window.opener) {
        try {
          window.opener.postMessage({ effectiveStatus: "campaign_linked", campaignId: selectedCampaign }, window.location.origin);
          setTimeout(() => {
            window.close();
          }, 1000);
        } catch (e) {
          window.opener.postMessage({ effectiveStatus: "campaign_linked", campaignId: selectedCampaign }, '*');
        }
      }
    }
  }

  return (
    <div className="text-left">
      <h3 className="text-md font-semibold text-gray-800 mb-3">Select an Ad Account</h3>
      <div className="space-y-2">
        {accounts.map((account) => (
          <button
            key={account.id}
            onClick={() => handleAccountSelect(account.id)}
            className={`w-full flex text-left p-3 rounded-md transition-colors text-sm ${selectedAccount === account.id
              ? 'bg-purple-50 text-[#442873] font-medium'
              : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
              }`}
          >
            {account.name || 'Unnamed Account'}
            {selectedAccount === account.id && (
              <svg className="w-2 h-2 ml-auto text-[#442873]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
              </svg>
            )}
          </button>
        ))}
      </div>

      {loadingCampaigns && (
        <div className="border-t border-gray-200 text-center flex flex-col items-center justify-center">
          <DotLoader />
          <p className="mt-3 text-sm text-gray-500">Loading campaigns...</p>
        </div>
      )}

      {selectedAccount && !loadingCampaigns && (
        <div className="pt-6 border-t border-gray-200">
          <h3 className="text-md font-semibold text-gray-800 mb-3">Campaigns</h3>

          {campaigns.length > 0 ? (
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                if (selectedCampaign) {
                  try {
                    await handleCampaignSelect(selectedCampaign, userId!);
                  } catch (err) {
                    console.error("Error selecting campaign:", err);
                  }
                }

              }}
            >
              <ul className="space-y-2 text-sm text-gray-700">
                {campaigns.map((campaign) => (
                  <li
                    key={campaign.id}
                    className="flex items-center p-2 rounded-md hover:bg-gray-50 cursor-pointer"
                  >
                    <input
                      type="radio"
                      id={`campaign-${campaign.id}`}
                      name="campaign"
                      value={campaign.id}
                      checked={selectedCampaign === campaign.id}
                      onChange={() => setSelectedCampaign(campaign.id)}
                      className="mr-3 accent-indigo-600"
                    />
                    <label
                      htmlFor={`campaign-${campaign.id}`}
                      className="cursor-pointer flex-1"
                    >
                      {campaign.name || "Unnamed Campaign"}
                    </label>
                  </li>
                ))}
              </ul>

              {selectedCampaign && (
                <div className="mt-4 text-right">
                  <button
                    type="submit"
                    className="px-4 py-2 bg-[#442873] text-white text-sm font-medium rounded-md hover:bg-[#442873] transition"
                  >
                    Confirm Selection
                  </button>
                </div>
              )}
            </form>
          ) : (
            <p className="text-sm text-gray-500 bg-gray-100 p-3 rounded-md">
              No campaigns found for this account.
            </p>
          )}
        </div>
      )}
    </div>
  );
};